import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Bell, ArrowRight, PackageOpen } from "lucide-react";
import { StatusBadge, PaymentBadge, CampaignProgress, Loading, EmptyState, Money } from "@/components/common";
import { formatDate, formatDateTime, pendingAmount } from "@/lib/format";
import { logoSrc } from "@/lib/nav";
import { Button } from "@/components/ui/button";

export function CustomerHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const logo = logoSrc(user);
  return (
    <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20">
      <div className="max-w-2xl mx-auto h-full px-4 flex items-center justify-between">
        <Link to="/portal" className="flex items-center gap-2.5 min-w-0">
          {logo ? (
            <img src={logo} alt="logo" className="h-8 w-8 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-blue-600 grid place-items-center text-white font-bold font-display shrink-0">
              {(user?.business_name || "V").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold font-display text-slate-900 leading-none truncate">{user?.business_name || "VYOMRA"}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Powered by VYOMRA</div>
          </div>
        </Link>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="customer-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 shrink-0">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </header>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    api.get("/orders").then(async (r) => {
      setOrders(r.data);
      const active = r.data.find((o) => o.status === "running") || r.data[0];
      if (active) {
        try { setUpdates((await api.get(`/orders/${active.order_id}/updates`)).data.slice(0, 3)); } catch {}
      }
    }).catch(() => setOrders(false));
  }, []);

  if (orders === null) return <div className="min-h-screen bg-slate-50"><CustomerHeader /><Loading /></div>;

  const active = orders && (orders.find((o) => o.status === "running") || orders.find((o) => o.status !== "completed" && o.status !== "cancelled") || orders[0]);

  return (
    <div className="min-h-screen bg-slate-50">
      <CustomerHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-sm text-slate-500 mt-1">Here's the latest on your campaigns.</p>
        </div>

        {!active ? (
          <div className="bg-white rounded-xl border p-5"><EmptyState icon={PackageOpen} title="No active orders yet" sub="Your orders will appear here once created." /></div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Active Order</div>
                  <div className="font-bold font-display text-lg text-slate-900 mt-0.5">{active.plan_name || active.business_name}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">{active.order_id}</div>
                </div>
                <StatusBadge status={active.status} />
              </div>
              <div className="mt-4"><CampaignProgress order={active} compact /></div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400 uppercase">Start</div><div className="font-medium text-slate-900">{formatDate(active.start_date)}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400 uppercase">End</div><div className="font-medium text-slate-900">{formatDate(active.end_date)}</div></div>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 p-3">
                <div><div className="text-xs text-slate-400 uppercase">Payment</div><div className="font-medium text-slate-900">Pending <Money value={pendingAmount(active)} /></div></div>
                <PaymentBadge status={active.payment_status} />
              </div>
              <Link to={`/portal/orders/${active.order_id}`} data-testid="view-active-order">
                <Button className="w-full mt-4 bg-[#1e3a8a] hover:bg-[#1e40af]">View Full Details <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </Link>
            </div>

            {updates.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3"><Bell className="h-4 w-4 text-blue-600" /><h3 className="font-semibold font-display text-slate-900">Latest Updates</h3></div>
                <ol className="space-y-3">
                  {updates.map((u) => (
                    <li key={u.id} className="border-l-2 border-blue-500 pl-3">
                      <div className="text-sm font-medium text-slate-900">{u.title}</div>
                      {u.message && <div className="text-sm text-slate-600">{u.message}</div>}
                      <div className="text-xs text-slate-400 mt-0.5">{formatDateTime(u.created_at)}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <Link to="/portal/orders" className="block text-center text-sm text-blue-700 font-medium hover:underline" data-testid="view-all-orders">View all my orders →</Link>
          </>
        )}
      </div>
    </div>
  );
}
